import React from 'react'
import { Link } from 'react-router-dom'
import { Event, EventCardMetaProps } from '../data/types'
import { CalendarIcon, ClockIcon, EyeHideIcon, EyeIcon, LocationIcon, MapIcon } from '../icons'
import { Card } from './ui/Card'
import { formatDateOnly, toDateInputValue } from '../utils/dates'

type Props = {
    event: Event
    meta: EventCardMetaProps
    showVisibility?: boolean
    [x: string]: any;
}

export const EventCard = (props: Props) => {
    const { event, meta, showVisibility = false } = props

    const formatDate = (dateStr: string) => {
        return formatDateOnly(dateStr, 'en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        })
    }

    const formatTime = (timeStr: string) => {
        const [hours, minutes] = timeStr.split(':')
        const hour = parseInt(hours)
        const ampm = hour >= 12 ? 'PM' : 'AM'
        const displayHour = hour % 12 || 12
        return `${displayHour}:${minutes} ${ampm}`
    }

    const isSameDay = toDateInputValue(event.start_date) === toDateInputValue(event.end_date)

    return (
        <Card>
            <div className="relative p-4 bg-repeat bg-tile bg-[url('/public/header_tile_gold.png')]">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-900/50 to-primary-400/30"></div>
                <div className="relative flex justify-between items-start">
                    <div>
                        <h3 className="text-xl font-bold text-white mb-1">{event.name}</h3>
                        <p className="text-primary-100 text-sm">{event.organizer}</p>
                    </div>
                    {/* Visibility indicator - only shown to admins */}
                    {showVisibility && (
                        <div
                            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                event.shifts_visible
                                    ? 'bg-success-100 text-success-700 dark:bg-success-900/50 dark:text-success-300'
                                    : 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300'
                            }`}
                            title={event.shifts_visible ? meta.shiftsVisibleTooltip : meta.shiftsPerAreaTooltip}
                        >
                            {event.shifts_visible ? (
                                <><EyeIcon className="w-3.5 h-3.5" /> {meta.visibleLabel}</>
                            ) : (
                                <><EyeHideIcon className="w-3.5 h-3.5" /> {meta.perAreaLabel}</>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <div className="flex items-start mb-3">
                            <CalendarIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 me-2 mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{meta.dateLabel}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {formatDate(event.start_date)}
                                    {!isSameDay && ` - ${formatDate(event.end_date)}`}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start">
                            <ClockIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 me-2 mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{meta.timeLabel}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {formatTime(event.start_time)} - {formatTime(event.end_time)}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-start mb-3">
                            <LocationIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 me-2 mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{meta.locationLabel}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{event.location}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-500">{event.city}, {event.country}</p>
                            </div>
                        </div>

                        <div className="flex items-start">
                            <MapIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 me-2 mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{meta.areasLabel}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{event.areas_count ?? 0} {(event.areas_count ?? 0) !== 1 ? meta.areasPlural : meta.areasSingular}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-500 mb-3">
                        <span className="font-semibold">{meta.addressLabel}:</span> {event.address}
                    </p>
                    <Link
                        to={`/events/${event.id}`}
                        className="block w-full text-center bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-4 rounded transition-colors duration-200"
                    >
                        {meta.viewDetailsButton}
                    </Link>
                </div>
            </div>
        </Card>
    )
}
